@import "tailwindcss";
@plugin "@tailwindcss/typography";

/* Enable class-based dark mode for Tailwind v4 */
@variant dark (&:where(.dark, .dark *));

/* Theme Tokens */
@theme {
  --color-primary-50: #e8f0ec;
  --color-primary-100: #c5dcd0;
  --color-primary-200: #9ec5ae;
  --color-primary-300: #77ad8c;
  --color-primary-400: #5a9672;
  --color-primary-500: #3d7f58;
  --color-primary-600: #1a6b42;
  --color-primary-700: #0F3D2E;
  --color-primary-800: #0a2e22;
  --color-primary-900: #071f17;
  --color-brand-accent: #E8B84C;
  --color-brand-forest: #0F3D2E;

  --font-sans: 'Inter', ui-sans-serif, system-ui, sans-serif;
  --font-mono: 'Fira Code', ui-monospace, monospace;

  --radius-sm: 0.375rem;
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --radius-xl: 1rem;
}

/* Base Styles */
@layer base {
  * {
    @apply border-gray-200;
  }

  body {
    @apply bg-white text-gray-900 antialiased;
    font-feature-settings: "rlig" 1, "calt" 1;
  }

  /* Custom Scrollbar */
  ::-webkit-scrollbar {
    @apply w-2 h-2;
  }

  ::-webkit-scrollbar-track {
    @apply bg-gray-100;
  }

  ::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full;
  }

  ::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }

  .dark ::-webkit-scrollbar-track {
    @apply bg-gray-800;
  }

  .dark ::-webkit-scrollbar-thumb {
    @apply bg-gray-600;
  }

  .dark ::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-500;
  }

  /* Focus Styles */
  button:focus-visible,
  a:focus-visible {
    @apply outline-none ring-2 ring-[#0F3D2E] ring-offset-2;
  }
}

/* Component Styles */
@layer components {
  .btn-primary {
    @apply inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0F3D2E] hover:bg-[#0d5236] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-800 transition-colors;
  }

  .btn-secondary {
    @apply inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-800 transition-colors;
  }

  .card {
    @apply bg-white rounded-lg shadow-sm border border-gray-200 p-6;
  }

  .nav-link {
    @apply text-gray-700 hover:text-emerald-800 px-3 py-2 rounded-md text-sm font-medium transition-colors;
  }
}

/* Utility Styles */
@layer utilities {
  .text-balance {
    text-wrap: balance;
  }

  .bg-grid-pattern {
    background-image: 
      linear-gradient(to right, rgb(229 231 235 / 0.1) 1px, transparent 1px),
      linear-gradient(to bottom, rgb(229 231 235 / 0.1) 1px, transparent 1px);
    background-size: 24px 24px;
  }

  .gradient-text {
    @apply bg-gradient-to-r from-[#0F3D2E] to-[#1a6b42] bg-clip-text text-transparent;
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-out;
}

.animate-slide-in {
  animation: slideIn 0.3s ease-out;
}

/* Nav pill→bar scroll animation */
.cf-nav-wrap {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  display: flex;
  justify-content: center;
  padding: 6px 20px;
  pointer-events: none;
  transition: padding 0.5s cubic-bezier(0.4,0,0.2,1);
}
.cf-nav-wrap.scrolled {
  padding: 0;
}
.cf-nav {
  pointer-events: all;
  width: 100%;
  max-width: 820px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 24px;
  border-radius: 14px;
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid transparent;
  box-shadow: none;
  transition:
    max-width     0.5s cubic-bezier(0.4,0,0.2,1),
    height        0.5s cubic-bezier(0.4,0,0.2,1),
    border-radius 0.5s cubic-bezier(0.4,0,0.2,1),
    background    0.5s cubic-bezier(0.4,0,0.2,1),
    border-color  0.5s cubic-bezier(0.4,0,0.2,1),
    box-shadow    0.5s cubic-bezier(0.4,0,0.2,1),
    padding       0.5s cubic-bezier(0.4,0,0.2,1);
}
.cf-nav-wrap.scrolled .cf-nav {
  max-width: 100%;
  height: 62px;
  border-radius: 0;
  padding: 0 36px;
  background: rgba(255,255,255,0.97);
  border-color: rgba(15,61,46,0.08);
  box-shadow: 0 1px 0 rgba(15,61,46,0.06), 0 8px 32px rgba(7,40,29,0.06);
}
.cf-nav-link {
  color: #374151;
  transition: color 0.2s;
}
.cf-nav-link:hover {
  color: #0F3D2E;
}

/* Blog post callout boxes */
.cf-blog-content blockquote {
  border-left: 3px solid #E8B84C;
  background: rgba(232, 184, 76, 0.08);
  border-radius: 0 8px 8px 0;
  padding: 12px 16px;
  margin: 24px 0;
  color: rgb(22, 30, 45);
  font-style: normal;
}
.cf-blog-content blockquote strong {
  color: #0F3D2E;
}
.cf-blog-content h2 {
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  scroll-margin-top: 80px;
}

/* Print Styles */
@media print {
  header,
  footer,
  nav {
    display: none;
  }
}